import type { SheetAnim, SheetDescription } from "odyssey-shared";

/**
 * How a layered character's layers stack (ADR-009, ADR-036 decision 4; the layered trial look, P1.9-T88). Pure: the
 * Phaser side (`character.ts`) only applies what this decides. Which strip its motion plays is the shared package's
 * `locomotionStrip`.
 *
 * Layers stack in the order they are named unless they are placed: a placed layer has a rank, and may have a table of
 * ranks by strip, direction and frame, which is how a weapon or a shield goes behind the body in one frame and in
 * front of it in the next. Layers of equal rank keep the order they were named in.
 */

/**
 * A layer's ranks frame by frame: by strip, then direction, one rank per frame.
 */
export type StripOrder = Readonly<Record<string, Readonly<Record<string, readonly number[]>>>>;

/**
 * Where a layer stacks: its rank, and the table that overrides it frame by frame, if any. Lower ranks draw first.
 */
export interface LayerPlace {
  readonly rank: number;
  readonly order: StripOrder | null;
}

/**
 * A look for the page's own character: the sheets to stack, bottom first, where each stacks, and how far the look's
 * standing art reaches above the feet in world pixels, where the nameplate stands.
 */
export interface OwnLook {
  readonly layers: readonly string[];
  readonly places: readonly LayerPlace[];
  readonly head: number;
}

/**
 * The rank a layer draws at in one frame.
 *
 * @param place Where the layer stacks.
 * @param anim The strip shown.
 * @param direction The direction shown.
 * @param frame The frame shown.
 * @returns The table's rank for the frame, or the layer's own rank when the table has none.
 */
export function layerRank(place: LayerPlace, anim: string, direction: string, frame: number): number {
  return place.order?.[anim]?.[direction]?.[frame] ?? place.rank;
}

/**
 * Sorts layer indices by rank, lowest first, layers of equal rank in index order, into a caller's array. Nothing is
 * allocated: a character calls it every frame.
 *
 * @param ranks Each layer's rank this frame, by layer index.
 * @param count How many layers there are.
 * @param out The layer indices in drawing order, rewritten in place; it holds the previous order on entry.
 * @returns Whether the order differs from the one `out` held.
 */
export function arrangeLayers(ranks: ArrayLike<number>, count: number, out: number[]): boolean {
  let changed = out.length !== count;
  let previous = -1;
  for (let position = 0; position < count; position += 1) {
    let best = -1;
    for (let index = 0; index < count; index += 1) {
      const rank = ranks[index] as number;
      const after = previous === -1 || rank > (ranks[previous] as number) || (rank === (ranks[previous] as number) && index > previous);
      if (!after) {
        continue;
      }
      if (best === -1 || rank < (ranks[best] as number)) {
        best = index;
      }
    }
    if (out[position] !== best) {
      out[position] = best;
      changed = true;
    }
    previous = best;
  }
  out.length = count;
  return changed;
}

/**
 * The strips a layered character can play: the bottom layer's, less any that another layer lacks. A layer that has
 * art only for walking (an outfit with no combat pages) so keeps the whole character to idle and walk: a strip a state
 * names and the character cannot play is drawn by its motion (ADR-036's amendment for P2.1-T4), every layer together,
 * and no layer is ever left on the frame of another strip.
 *
 * @param layers Each layer's strips, bottom first.
 * @returns The strips every layer has, with the bottom layer's rates and frame counts; none with no layers.
 */
export function sharedStrips(layers: readonly Readonly<Record<string, SheetAnim>>[]): Readonly<Record<string, SheetAnim>> {
  const bottom = layers[0];
  if (bottom === undefined) {
    return {};
  }
  const shared: Record<string, SheetAnim> = {};
  for (const [name, anim] of Object.entries(bottom)) {
    if (layers.every((layer) => layer[name] !== undefined)) {
      shared[name] = anim;
    }
  }
  return shared;
}

/**
 * What loads a look for the page's own character: its sheets by id and the look.
 */
export type OwnLookSource = (base: string) => Promise<{ readonly sheets: Readonly<Record<string, SheetDescription>>; readonly look: OwnLook }>;

/**
 * Adds the sheets of the own character's look to the character manifest, when the page asked for one. A look that
 * does not load (its manifest absent or malformed, or a choice it lacks) is warned of and left out, so the page still
 * starts with the default body.
 *
 * @param manifest The character manifest as served.
 * @param load The look's loader, or undefined when the page asked for none.
 * @param base The asset base the page loads from.
 * @param warn Where the warning goes.
 * @returns The manifest with the look's sheets and the look, or the manifest as served and null.
 */
export async function withOwnLook<T extends { readonly characters: Readonly<Record<string, SheetDescription>> }>(manifest: T, load: OwnLookSource | undefined, base: string, warn: (line: string) => void): Promise<{ readonly manifest: T; readonly look: OwnLook | null }> {
  if (load === undefined) {
    return { manifest, look: null };
  }
  try {
    const loaded = await load(base);
    return { manifest: { ...manifest, characters: { ...manifest.characters, ...loaded.sheets } }, look: loaded.look };
  } catch (error) {
    warn(`the trial look is not worn: ${error instanceof Error ? error.message : String(error)}`);
    return { manifest, look: null };
  }
}

/**
 * Makes the page's own character: the layered one when the boot loaded a look, the plain one every other player is
 * drawn with when it did not.
 *
 * @param look The loaded look, or null.
 * @param plain Makes the default character.
 * @param layered Makes a character from a look.
 * @returns The character.
 */
export function ownOrPlain<T>(look: OwnLook | null, plain: () => T, layered: (look: OwnLook) => T): T {
  return look === null ? plain() : layered(look);
}
